import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
    Users, Clock, Euro, MapPin, Wifi, Monitor, 
    Coffee, ArrowRight, Calendar, Building2, Check, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Room images - using local assets
const ROOM_IMAGES = {
    'Seeblick': {
        main: '/assets/seeblick/1.jpg',
        gallery: [
            '/assets/seeblick/1.jpg',
            '/assets/seeblick/2.jpg',
            '/assets/seeblick/3.jpg',
            '/assets/seeblick/4.jpg',
            '/assets/seeblick/5.jpg',
            '/assets/seeblick/6.jpg',
            '/assets/seeblick/7.jpg',
            '/assets/seeblick/8.jpg'
        ]
    },
    'Waldblick': {
        main: '/assets/waldblick/1.jpg',
        gallery: [
            '/assets/waldblick/1.jpg',
            '/assets/waldblick/2.jpg',
            '/assets/waldblick/3.jpg',
            '/assets/waldblick/4.jpg',
            '/assets/waldblick/5.jpg'
        ]
    }
};

// Amenity icon mapping
const amenityIcons = {
    'beamer': Monitor,
    'wlan': Wifi,
    'wifi': Wifi,
    'kaffeemaschine': Coffee,
    'kaffee': Coffee,
    'default': Check
};

const getAmenityIcon = (amenity) => {
    const lowerAmenity = amenity.toLowerCase();
    for (const [key, Icon] of Object.entries(amenityIcons)) {
        if (lowerAmenity.includes(key)) return Icon;
    }
    return amenityIcons.default;
};

export const PublicBooking = () => {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    
    // Touch/Swipe state for lightbox
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;
    
    // Flow animation state
    const [slideDirection, setSlideDirection] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        fetchRooms();
    }, []);

    const fetchRooms = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/rooms`);
            setRooms(response.data.filter(r => r.is_active !== false));
        } catch (error) {
            console.error('Error fetching rooms:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get images for a room
    const getRoomImages = (roomName) => {
        return ROOM_IMAGES[roomName] || { main: null, gallery: [] };
    };

    // Touch handlers for swipe
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe) {
            nextImage();
        } else if (isRightSwipe) {
            prevImage();
        }
        setTouchStart(null);
        setTouchEnd(null);
    };

    // Lightbox functions
    const openLightbox = (images, startIndex = 0) => {
        setLightboxImages(images);
        setLightboxIndex(startIndex);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
        
        // Preload adjacent images
        [startIndex, (startIndex + 1) % images.length, (startIndex - 1 + images.length) % images.length].forEach(idx => {
            const img = new Image();
            img.src = images[idx];
        });
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = '';
    };

    const nextImage = () => {
        if (isAnimating) return;
        setSlideDirection('left');
        setIsAnimating(true);
        setTimeout(() => {
            const newIndex = (lightboxIndex + 1) % lightboxImages.length;
            setLightboxIndex(newIndex);
            // Preload next image
            const preloadIdx = (newIndex + 1) % lightboxImages.length;
            const img = new Image();
            img.src = lightboxImages[preloadIdx];
            setSlideDirection(null);
            setIsAnimating(false);
        }, 250);
    };

    const prevImage = () => {
        if (isAnimating) return;
        setSlideDirection('right');
        setIsAnimating(true);
        setTimeout(() => {
            const newIndex = (lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length;
            setLightboxIndex(newIndex);
            // Preload previous image
            const preloadIdx = (newIndex - 1 + lightboxImages.length) % lightboxImages.length;
            const img = new Image();
            img.src = lightboxImages[preloadIdx];
            setSlideDirection(null);
            setIsAnimating(false);
        }, 250);
    };

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            
            {/* Page Content with top padding for fixed navbar */}
            <div className="pt-24">
                {/* Page Title */}
                <div className="bg-black text-white py-12 px-4">
                    <div className="max-w-6xl mx-auto text-center">
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">Raumbuchung</h1>
                        <p className="text-base text-gray-300 max-w-2xl mx-auto">
                            Professionelle Besprechungsräume für Ihre Meetings, Workshops und Veranstaltungen
                        </p>
                    </div>
                </div>

            {/* Rooms Grid - Kompakter */}
            <section className="py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-gray-500">Räume werden geladen...</p>
                        </div>
                    ) : rooms.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500">Aktuell keine Räume verfügbar</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-6">
                            {rooms.map(room => {
                                const roomImages = getRoomImages(room.name);
                                const mainImage = roomImages.main || room.image_url;
                                const allImages = mainImage ? [mainImage, ...roomImages.gallery] : roomImages.gallery;
                                
                                return (
                                <Card key={room._id} className="overflow-hidden group hover:shadow-xl transition-shadow flex flex-col">
                                    {/* Room Image with Gallery - Kleiner */}
                                    <div className="relative h-48 bg-gray-100 overflow-hidden">
                                        {mainImage ? (
                                            <img 
                                                src={mainImage} 
                                                alt={room.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                                                onClick={() => allImages.length > 0 && openLightbox(allImages, 0)}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                                                    e.target.parentElement.innerHTML = `<div class="text-center text-gray-400"><svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>${room.name}</div>`;
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                                                <div className="text-center text-gray-400">
                                                    <Building2 className="w-12 h-12 mx-auto mb-2" />
                                                    <span>{room.name}</span>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Gallery indicator */}
                                        {allImages.length > 1 && (
                                            <div 
                                                className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-black/90 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openLightbox(allImages, 0);
                                                }}
                                            >
                                                📷 {allImages.length} Bilder
                                            </div>
                                        )}
                                        
                                        {/* Price Badge */}
                                        <div className="absolute top-3 right-3 bg-black text-white px-3 py-1 rounded-full">
                                            {room.name?.toLowerCase().includes('seeblick') ? (
                                                <>
                                                    <span className="text-xs text-gray-300">ab </span>
                                                    <span className="text-base font-bold">{room.external_half_day_price || 249}€</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-base font-bold">{room.price_per_hour}€</span>
                                                    <span className="text-xs text-gray-300">/Std</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <CardContent className="p-4 flex flex-col flex-grow">
                                        <h3 className="text-xl font-bold mb-1">{room.name}</h3>
                                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{room.description}</p>

                                        {/* Info Row */}
                                        <div className="flex flex-wrap gap-3 mb-3 text-xs">
                                            <span className="flex items-center gap-1 text-gray-600">
                                                <Users size={14} />
                                                bis {room.capacity} Personen
                                            </span>
                                            <span className="flex items-center gap-1 text-gray-600">
                                                <MapPin size={14} />
                                                teamWERK Erolzheim
                                            </span>
                                        </div>

                                        {/* Amenities - Kompakter */}
                                        {room.amenities?.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mb-3">
                                                {room.amenities.slice(0, 4).map((amenity, i) => {
                                                    const Icon = getAmenityIcon(amenity);
                                                    return (
                                                        <span 
                                                            key={i} 
                                                            className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-0.5 rounded-full"
                                                        >
                                                            <Icon size={12} />
                                                            {amenity}
                                                        </span>
                                                    );
                                                })}
                                                {room.amenities.length > 4 && (
                                                    <span className="text-xs text-gray-500">
                                                        +{room.amenities.length - 4}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Booking Options - Kompakter */}
                                        {room.booking_options?.length > 0 && (
                                            <div className="border-t pt-3 mb-3">
                                                <p className="text-xs text-gray-500 mb-1.5">Buchungsoptionen:</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {room.booking_options.filter(o => o.is_active).map((opt, i) => (
                                                        <span key={i} className="text-xs bg-black text-white px-2 py-0.5 rounded-full">
                                                            {opt.name}: {opt.price}€
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* VAT Notice */}
                                        <p className="text-xs text-gray-400 mb-2">Alle Preise zzgl. 19% MwSt.</p>

                                        {/* Spacer to push button to bottom */}
                                        <div className="flex-grow"></div>

                                        {/* CTA Button - Always at bottom */}
                                        <Button 
                                            className="w-full bg-black hover:bg-gray-800 text-white py-4 text-base mt-2"
                                            onClick={() => {
                                                const name = room.name?.toLowerCase() || '';
                                                if (name.includes('seeblick')) {
                                                    navigate('/raum-buchen/seeblick');
                                                } else if (name.includes('waldblick')) {
                                                    navigate('/raum-buchen/waldblick');
                                                } else {
                                                    navigate(`/buchung/${room._id}`);
                                                }
                                            }}
                                        >
                                            <Calendar className="mr-2 h-4 w-4" />
                                            Jetzt buchen
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </CardContent>
                                </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* Info Section - Kompakter */}
            <section className="py-8 px-4 bg-gray-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-2xl font-bold mb-2">Für teamWERK Mieter</h2>
                    <p className="text-gray-600 text-sm mb-4">
                        Als Mieter im teamWERK können Sie die Besprechungsräume <strong>kostenlos</strong> nutzen. 
                        Registrieren Sie sich mit Ihrer E-Mail und wir schalten Sie als internen Nutzer frei.
                    </p>
                    <Button 
                        variant="outline" 
                        className="border-black text-black hover:bg-black hover:text-white"
                        onClick={() => navigate('/login')}
                    >
                        Jetzt registrieren
                    </Button>
                </div>
            </section>
            </div> {/* End of pt-24 wrapper */}

            {/* Lightbox */}
            {lightboxOpen && (
                <div 
                    className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
                    onClick={closeLightbox}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Close button */}
                    <button 
                        className="absolute top-4 right-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10 bg-black/30"
                        onClick={closeLightbox}
                    >
                        <X size={32} />
                    </button>
                    
                    {/* Swipe hint on mobile */}
                    {lightboxImages.length > 1 && (
                        <div className="md:hidden absolute top-4 left-4 text-white/60 text-xs bg-black/30 px-3 py-2 rounded-full">
                            ← Wischen zum Navigieren →
                        </div>
                    )}
                    
                    {/* Navigation - Hidden on mobile, use swipe instead */}
                    {lightboxImages.length > 1 && (
                        <>
                            <button 
                                className="hidden md:block absolute left-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
                                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            >
                                <ChevronLeft size={40} />
                            </button>
                            <button 
                                className="hidden md:block absolute right-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
                                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            >
                                <ChevronRight size={40} />
                            </button>
                            
                            {/* Mobile touch areas */}
                            <button 
                                className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 w-16 h-32 flex items-center justify-start pl-2 text-white/50 active:text-white active:bg-white/10 z-10"
                                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            >
                                <ChevronLeft size={28} />
                            </button>
                            <button 
                                className="md:hidden absolute right-0 top-1/2 -translate-y-1/2 w-16 h-32 flex items-center justify-end pr-2 text-white/50 active:text-white active:bg-white/10 z-10"
                                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            >
                                <ChevronRight size={28} />
                            </button>
                        </>
                    )}
                    
                    {/* Loading Spinner */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="animate-spin w-10 h-10 border-4 border-white/30 border-t-white rounded-full" id="lightbox-spinner"></div>
                    </div>
                    
                    {/* Image with Flow Animation */}
                    <img 
                        key={lightboxIndex}
                        src={lightboxImages[lightboxIndex]} 
                        alt={`Bild ${lightboxIndex + 1}`}
                        className={`max-h-[85vh] max-w-[90vw] object-contain select-none transition-all duration-300 ease-out
                            ${slideDirection === 'left' ? 'animate-slide-out-left' : ''}
                            ${slideDirection === 'right' ? 'animate-slide-out-right' : ''}
                            ${!slideDirection ? 'animate-slide-in' : ''}
                        `}
                        onClick={(e) => e.stopPropagation()}
                        draggable="false"
                        onLoad={(e) => {
                            const spinner = document.getElementById('lightbox-spinner');
                            if (spinner) spinner.style.display = 'none';
                        }}
                    />
                    
                    {/* Flow Animation Styles */}
                    <style>{`
                        @keyframes slide-out-left {
                            from { transform: translateX(0) scale(1); opacity: 1; }
                            to { transform: translateX(-100px) scale(0.9); opacity: 0; }
                        }
                        @keyframes slide-out-right {
                            from { transform: translateX(0) scale(1); opacity: 1; }
                            to { transform: translateX(100px) scale(0.9); opacity: 0; }
                        }
                        @keyframes slide-in {
                            from { transform: scale(0.95); opacity: 0; }
                            to { transform: scale(1); opacity: 1; }
                        }
                        .animate-slide-out-left { animation: slide-out-left 0.25s ease-out forwards; }
                        .animate-slide-out-right { animation: slide-out-right 0.25s ease-out forwards; }
                        .animate-slide-in { animation: slide-in 0.3s ease-out forwards; }
                    `}</style>
                    
                    {/* Counter */}
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
                        {lightboxIndex + 1} / {lightboxImages.length}
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default PublicBooking;
